// deps
import React, { memo, useMemo } from "react";

// helpers
import AriaHidden from "@core-ui/components/accessibility/aria-hidden/AriaHidden";

// interface
import { TurboStripData, TurboStripType } from "@core-ui/interfaces/index";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// constants

// styles
import { AnimatedText, AnimatedTextContainer, AriaHiddenAnimatedText, StripText } from "../styles";

// hooks
import { useShowAnimation } from "../useShowAnimation";
import { getTextVariantAndColor } from "../utils";

export interface ITurboStripContentProps {
    data: TurboStripData;
}

const TurboStripContent = ({ data }: ITurboStripContentProps) => {
    const { stripMsg = "", isNewBanner = true, animation, type = TurboStripType.STATIC } = data;

    const { hasCompletedAnimation, isExiting, isActive, currentMessage, nextMessage, shouldAnimate } = useShowAnimation(
        {
            isNewBanner,
            animation,
            stripMsg,
        },
    );

    const { textVariant, patternTextVariant, textColor, patternTextColor } = useMemo(() => {
        return getTextVariantAndColor(type);
    }, [type]);

    const renderText = (message: string | undefined) => {
        if (!message) return null;

        // Split the message by the pattern [[...]]
        const parts = message.split(/(\[\[.*?\]\])/);

        return parts.map((part, index) => {
            // Check if this part matches the pattern [[...]]
            if (part.match(/\[\[.*?\]\]/)) {
                // Extract the content inside [[]]
                const content = part.replace(/\[\[|\]\]/g, "");
                return (
                    <Text as="span" key={index} variant={patternTextVariant} color={patternTextColor}>
                        {content}
                    </Text>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    if (hasCompletedAnimation || !shouldAnimate) {
        const displayMessage = isNewBanner && animation?.messageList?.[0] ? animation.messageList[0] : stripMsg;

        return (
            <AriaHidden as="div">
                <StripText variant={textVariant} color={textColor} type={type}>
                    {renderText(displayMessage)}
                </StripText>
            </AriaHidden>
        );
    }

    return (
        <AriaHiddenAnimatedText as="div">
            <AnimatedTextContainer isAnimating={shouldAnimate}>
                <AnimatedText
                    isActive={isActive}
                    isExiting={isExiting}
                    color="text_High_Emphasis"
                    variant="V2.body3.neutral.medium"
                >
                    {renderText(currentMessage)}
                </AnimatedText>
                {shouldAnimate && nextMessage && (
                    <AnimatedText
                        isActive={!isActive && !isExiting}
                        isExiting={false}
                        color="text_High_Emphasis"
                        variant="V2.body3.neutral.medium"
                    >
                        {renderText(nextMessage)}
                    </AnimatedText>
                )}
            </AnimatedTextContainer>
        </AriaHiddenAnimatedText>
    );
};

export default memo(TurboStripContent);
